<template>
  <section
    class="wmp-panel"
    :class="{ 'wmp-panel--profile': isProfile, 'wmp-panel--readiness': isReadiness }"
    :aria-label="ariaLabel"
    data-testid="welcome-wizard-media-panel"
  >
    <div v-if="isProfile" class="wmp-preview" data-testid="wmp-profile-preview">
      <p class="wmp-preview-name" data-testid="wmp-preview-name">
        <span v-if="previewFirstName" class="wmp-preview-line">{{ previewFirstName }}</span>
        <span v-if="previewLastName" class="wmp-preview-line">{{ previewLastName }}</span>
      </p>
      <img
        v-if="previewPhoto"
        class="wmp-preview-photo"
        :src="previewPhoto"
        alt="Profile picture preview"
        data-testid="wmp-preview-photo"
      />
      <div v-else class="wmp-preview-empty" data-testid="wmp-preview-empty">
        <span class="wmp-preview-avatar" aria-hidden="true">
          <svg
            width="32"
            height="32"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M20 21C20 19.6044 20 18.9067 19.8278 18.3389C19.4312 17.0305 18.4056 16.0049 17.0972 15.6083C16.5294 15.4361 15.8316 15.4361 14.4361 15.4361H9.56389C8.16844 15.4361 7.47071 15.4361 6.90285 15.6083C5.59437 16.0049 4.5688 17.0305 4.17222 18.3389C4 18.9067 4 19.6044 4 21M16.5 7.5C16.5 9.98528 14.4853 12 12 12C9.51472 12 7.5 9.98528 7.5 7.5C7.5 5.01472 9.51472 3 12 3C14.4853 3 16.5 5.01472 16.5 7.5Z"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </span>
        <span class="wmp-preview-hint">Your headshot</span>
      </div>
    </div>

    <template v-else-if="isReadiness">
      <ul class="wmp-ladder" data-testid="wmp-ladder">
        <li
          v-for="(level, index) in levels"
          :key="level.name"
          class="wmp-step"
          data-testid="wmp-step"
        >
          <ReadinessBadge
            :level="level.name"
            view="ReadOnly"
            hide-chatter-icon
            data-testid="wmp-badge"
          />
          <div class="wmp-block" :class="`wmp-block--${index + 1}`" data-testid="wmp-block">
            {{ level.tag }}
          </div>
        </li>
      </ul>
      <p v-if="caption" class="wmp-ladder-caption" data-testid="wmp-ladder-caption">
        {{ caption }}
      </p>
    </template>

    <template v-else>
      <div class="wmp-video" data-testid="wmp-video">
        <iframe
          v-if="videoEmbedUrl"
          class="wmp-iframe"
          :src="videoEmbedUrl"
          :title="title || 'Wizard video'"
          allow="fullscreen; autoplay; picture-in-picture"
          allowfullscreen
          data-testid="wmp-iframe"
        ></iframe>
        <div v-else class="wmp-placeholder" data-testid="wmp-placeholder">
          <span class="wmp-play" aria-hidden="true" data-testid="wmp-play">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M8.5 5.5L17 12L8.5 18.5V5.5Z" fill="currentColor" />
            </svg>
          </span>
        </div>
      </div>

      <div class="wmp-copy">
        <h3 v-if="title" class="wmp-title" data-testid="wmp-title">{{ title }}</h3>
        <p v-if="caption" class="wmp-caption" data-testid="wmp-caption">{{ caption }}</p>
      </div>
    </template>
  </section>
</template>

<script setup>
import { computed } from "vue";
import ReadinessBadge from "@components/ReadinessBadge/ReadinessBadge.vue";

/**
 * ER-12245 — WelcomeWizardMediaPanel (MDP-621).
 *
 * The right-hand media column of the onboarding wizards: a 16:9 Clueso video embed
 * (the permanent watchclueso.com iframe supplied by Eric) with its title and caption
 * beneath. When no embed URL is available yet (e.g. a step whose video is still being
 * produced) a neutral placeholder with a play glyph renders instead. The `profile`
 * content variant (Figma "Content=Profile preview") swaps the video for the badge
 * preview card used by Set your profile: the name stacked above a large rounded
 * photo, or the avatar fallback when there is no photo. The `readiness` variant (Figma
 * "Content=Readiness badges") shows the badge ladder: one stair block per level with
 * its ReadinessBadge above it and the level tag inside. Presentational only — the
 * embed URL, copy and preview values arrive as props.
 */
const props = defineProps({
  /** What the panel shows: the step video, the profile badge preview card or the badge ladder. */
  content: {
    type: String,
    default: "video",
    validator: (value) => ["video", "profile", "readiness"].includes(value),
  },
  /** Readiness ladder: `{ name, tag }` per level, lowest first. */
  levels: {
    type: Array,
    default: () => [],
  },
  /** Profile preview: first name, shown on the first line of the card. */
  previewFirstName: {
    type: String,
    default: "",
  },
  /** Profile preview: last name, shown beneath the first name. */
  previewLastName: {
    type: String,
    default: "",
  },
  /** Profile preview: photo URL; empty shows the avatar fallback. */
  previewPhoto: {
    type: String,
    default: "",
  },
  /** watchclueso.com embed URL (permanent; re-publishing updates the same link). */
  videoEmbedUrl: {
    type: String,
    default: "",
  },
  /** Video title shown beneath the player. */
  title: {
    type: String,
    default: "",
  },
  /** Video caption (e.g. "Welcome as Employee first page · 1:27"). */
  caption: {
    type: String,
    default: "",
  },
});

const isProfile = computed(() => props.content === "profile");
const isReadiness = computed(() => props.content === "readiness");
const ariaLabel = computed(() => {
  if (isProfile.value) return "Profile badge preview";
  if (isReadiness.value) return "Readiness badge levels";
  return props.title || "Video";
});
</script>

<style scoped>
.wmp-panel {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
  padding: 48px;
  background: #eaecf0;
  border-radius: 16px;
}

.wmp-video {
  position: relative;
  overflow: hidden;
  width: 100%;
  aspect-ratio: 16 / 9;
  background: #101828;
  border-radius: 8px;
}

.wmp-iframe {
  display: block;
  width: 100%;
  height: 100%;
  border: none;
}

.wmp-placeholder {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
}

.wmp-play {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 56px;
  height: 56px;
  background: #ffffff;
  border-radius: 50%;
  color: #7f56d9;
  box-shadow: 0 4px 12px rgba(16, 24, 40, 0.2);
}

.wmp-copy {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.wmp-title {
  margin: 0;
  color: #344054;
  font-size: 18px;
  font-weight: 600;
  line-height: 28px;
}

.wmp-caption {
  margin: 0;
  color: #475467;
  font-size: 14px;
  line-height: 20px;
}

.wmp-panel--profile,
.wmp-panel--readiness {
  align-items: center;
  justify-content: center;
}

.wmp-ladder {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  align-items: end;
  gap: 8px;
  width: 100%;
  margin: 0;
  padding: 0;
  list-style: none;
}

.wmp-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.wmp-block {
  box-sizing: border-box;
  width: 100%;
  padding: 12px 12px 12px 16px;
  border-radius: 12px 12px 0 0;
  color: #101828;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
}

.wmp-block--1 {
  min-height: 112px;
  background: #e88a0d;
}

.wmp-block--2 {
  min-height: 176px;
  background: #12b86b;
}

.wmp-block--3 {
  min-height: 224px;
  background: #0aa6ed;
}

.wmp-block--4 {
  min-height: 288px;
  background: #3d1cc7;
  color: #ffffff;
}

.wmp-ladder-caption {
  margin: 0;
  color: #344054;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
}

.wmp-preview {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 304px;
  max-width: 100%;
  padding: 20px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 12px 24px -4px rgba(15, 23, 42, 0.08);
}

.wmp-preview-name {
  min-height: 68px;
  margin: 0;
  color: #101828;
  font-size: 28px;
  font-weight: 500;
  line-height: 34px;
  overflow-wrap: anywhere;
}

.wmp-preview-line {
  display: block;
}

.wmp-preview-photo {
  display: block;
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  border-radius: 8px;
}

.wmp-preview-empty {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: #f9fafb;
  border: 1px dashed #d0d5dd;
  border-radius: 8px;
}

.wmp-preview-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  background: #f2f4f7;
  border-radius: 50%;
  color: #667085;
}

.wmp-preview-hint {
  color: #475467;
  font-size: 14px;
  font-weight: 500;
  line-height: 17px;
}
</style>
